import { Fragment, ReactNode } from 'react';
import type { RouteComponentProps, RouteProps } from 'react-router-dom';
import { Redirect, Route, useParams } from 'react-router-dom';
import { LOGIN_ROUTE } from '@upheal/shared/routes';
import { useAppState } from '@upheal/shared/state';
import { User, UserRole } from '@upheal/shared/types';

// We wanna make sure the component fully re-mounts (and therefore resets its state), when route changes (eg. switching from client A to client B).
const ComponentKeyWrapper = ({ children }: { children: ReactNode }) => {
    const params = useParams();

    return <Fragment key={JSON.stringify(params)}>{children}</Fragment>;
};

export type SignInLocationState =
    | {
          from?: { pathname?: string; search?: string };
          config?: { theme?: 'light' | 'dark' };
      }
    | null
    | undefined;

type RenderProps = RouteComponentProps<any> & {
    user: User;
};

type Props = {
    loginPage?: {
        theme?: 'dark' | 'light';
    };
    render?: (props: RenderProps) => ReactNode;
    requiredRole?: UserRole[];
} & Omit<RouteProps, 'render'>;

const RequireAuth = ({
    component: Component,
    render,
    loginPage,
    requiredRole,
    ...rest
}: Props) => {
    const { isAuthed, user } = useAppState();
    const hasSufficientRole =
        requiredRole && user ? requiredRole.includes(user?.role) : true;
    const isAuthenticated = isAuthed && user && user.role !== UserRole.Client;
    const allowRoute = isAuthenticated && hasSufficientRole;

    if (!Component && !render) {
        throw new Error('Component or render method is required');
    }

    const authedRedirect = (props: RenderProps) => {
        if (Component) {
            return <Component {...props} />;
        } else if (render) {
            return render(props);
        } else {
            throw new Error('Component or render method is required');
        }
    };

    return (
        <Route
            {...rest}
            render={(props) =>
                allowRoute ? (
                    <ComponentKeyWrapper>
                        {authedRedirect({ ...props, user })}
                    </ComponentKeyWrapper>
                ) : (
                    <Redirect
                        to={{
                            pathname: LOGIN_ROUTE,
                            state: {
                                from: props.location,
                                config: loginPage,
                            } as SignInLocationState,
                        }}
                    />
                )
            }
        />
    );
};

export default RequireAuth;
